import * as React from "react";
import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import * as CSIDActions from "../../../store/csid/actions";
import { IAddress } from "../../../store/csid/types";
import { ApplicationState } from "../../../store";


interface IProps {
    closeButtonText: string;
    onCloseCallback: any;
    openAsDialog: boolean;
    submitButtonText: string;
    onSubmitCallback: any;
    errorCallback: any;
    telephone?: string;
    ssn: string;
    dob?: string;
    address?: IAddress;
    postponeActivation: boolean;
    tuTrustedSessionId?: string;
    maxTUOTPAllowed: number;
    loadedResponse?: any;
}

interface IL3Question {
    "question-id": string;
    "question-text": string;
    options: IL3Option[];
}

interface IL3Option {
    "option-id": string;
    "option-text": string;
}

interface IL3QuestionOption {
    "question-id": string;
    "option-id": string;
}

interface IDFL3QuestionDTO {
    "auth-status": string;
    "status-code": string;
    message: string;
    questions: IL3Question[];
}

const L3OTPOptionComponent: React.FunctionComponent<IProps> = (props) => {
    const status = {
        success: "success",
        error: "error",
        systemError: "system-error",
        l3LastTryFail: "l3-last-try-fail",
        l3LastTryTimemout: "l3-timeout-locked",
        l3AttemptFail: "l3-attempt-fail",
        l3Timeout: "l3-timeout",
        thinClient: "thin-client",
        manualAuthentication: "manual-authentication",
        locked: "locked",
        l3OtpLocked : "l3-otp-locked",
        inProgress: "in-progress",
        invalidSsn: "invalid-ssn",
        noQuestion: "no-question",
        lockedNotEnoughInfo: "locked-not-enough-info",
        overTUOTPRequest: "over-tu-otp-request",
    };
    const isTUUser = useSelector((state: ApplicationState) => state.loggedInUser && state.loggedInUser.userInfo?.isTUUser);
    const initialQuestions = [
        {
            "question-id": "",
            "question-text": "",
            options: [
                {
                    "option-id": "",
                    "option-text": "",
                },
            ],
        },
    ];

    const [isLoading, setIsLoading] = useState(false);

    const dispatch = useDispatch();

    const [questions, setQuestions] = useState(initialQuestions);

    const setGlobalTUOTPRequestedCount = (requestCount: number) => dispatch(CSIDActions.actionCreators.setGlobalTUOTPRequestedCount(requestCount));

    let strCloseButtonText = "Close";
    let blnOpenAsDialog = false;

    // set default value for props, if not passed
    if (props.closeButtonText != null) {
        strCloseButtonText = props.closeButtonText;
    }

    if (props.openAsDialog != null) {
        blnOpenAsDialog = props.openAsDialog;
    }

    const getL3SecurityQuestions = (responseObj: any) => {
        if (responseObj.providerResponse != null) {
            const numberOfAttempts = responseObj.providerResponse["number-of-attempts"];
            setGlobalTUOTPRequestedCount(numberOfAttempts);
              
            if (numberOfAttempts && numberOfAttempts > props.maxTUOTPAllowed) {
                props.errorCallback({ status: status.overTUOTPRequest, data: { reason: status.overTUOTPRequest, detail: responseObj } });
            } else if (responseObj.providerResponse.questions != null) {
                var question = responseObj.providerResponse as IDFL3QuestionDTO;
                setQuestions(question.questions);
            } else if (!responseObj.isValidSsn) {
                props.errorCallback({ status: status.invalidSsn, data: { reason: status.invalidSsn, detail: responseObj } });
            } else {
                const authStatus = responseObj.providerResponse["auth-status"];
                if (authStatus === "locked") {
                  props.errorCallback({ status: status.locked, data: { reason: status.locked, detail: responseObj } });
                } else if (authStatus ==="l3-otp-locked") {
                    props.errorCallback({ status: status.l3OtpLocked, data: { reason: status.l3OtpLocked, detail: responseObj } });
                } else if (authStatus === "locked-not-enough-info") {
                    props.errorCallback({ status: status.lockedNotEnoughInfo, data: { reason: status.lockedNotEnoughInfo, detail: responseObj } });
                } else if (authStatus === "no-question") {
                    props.errorCallback({ status: status.noQuestion, data: { reason: status.noQuestion, detail: responseObj } });
                }
            }
        } else {
            if (props.errorCallback != null) {
                props.errorCallback({ status: status.error, data: { reason: status.systemError, detail: responseObj } });
             }
        }
    };

    const getAnswerForOTPOption = function (questions: IL3Question[], selectedOptionText: string): IL3QuestionOption {
        let questionId: string = "";
        let optionId: string = "";

        if (questions && questions.length > 0) {
            let questionObj: IL3Question = questions[0];

            questionId = questionObj["question-id"];
            let optionSelected: any = null;
            for (let i = 0; i < questionObj.options.length; i++) {
                if (questionObj.options[i]["option-text"] === selectedOptionText) {
                    optionSelected = questionObj.options[i];
                }
            }
            optionId = optionSelected["option-id"];
        }

        return {
            "question-id": questionId,
            "option-id": optionId,
        };
    };
    
    const submitOTPInputOption = (selectedOptionText: string) => {
        let l3QuestionOption: IL3QuestionOption = getAnswerForOTPOption(questions, selectedOptionText);

        setIsLoading(true);
        invokePostSubmitCallback("success", l3QuestionOption, selectedOptionText);
    };

    // handle close button click
    // add reason, so that the callback function can identify, if it's cancel (incase of dialog: ESC press) or button click close
    const onClose = (reason?: string) => {
        // execute method passed as props for onCloseButtonClick
        if (props.onCloseCallback != null) {
            props.onCloseCallback({ reason: reason == null ? "close" : "cancel"});
        }
    };

    const invokePostSubmitCallback = (status: string, questionOption: IL3QuestionOption, selectedOptionText: string) => {
        if (props.onSubmitCallback != null) {
            props.onSubmitCallback({ status, questionOption, selectedOptionText });
        }
    };

    const ensureDataFetchedOnLoad = () => {
        getL3SecurityQuestions(props.loadedResponse);
    };

    useEffect(ensureDataFetchedOnLoad, []);

    // in case of dialog, register event for escape press
    if (blnOpenAsDialog) {
        window.addEventListener("keyup", (evt: any) => {
            evt = evt || window.event;
            var isEscape = false;
            if ("key" in evt) {
                isEscape = evt.key === "Escape" || evt.key === "Esc";
            } else {
                isEscape = evt.keyCode === 27;
            }
            if (isEscape) {
                window.removeEventListener("keyup", () => {});
                onClose("cancel");
            }
        });
    }

    const onSubmitTextOption = (event: any) => {
        event.preventDefault();
        if (!isLoading) {
            const optionText = "Deliver passcode via Text Message";
            submitOTPInputOption(optionText);
        }
    };

    const onSubmitCallOption = (event: any) => {
        event.preventDefault();
        if (!isLoading) {
            const optionText = "Deliver passcode via Voice Call";
            submitOTPInputOption(optionText);
        }
    };

    return (
        <React.Fragment>
            <div className={`${isLoading && "loading"}`}>
                <div className="dialog-header otp-dialog-header">
                    {props.openAsDialog && (
                        <div className="dialog-icon status-default">
                            <em className="las la-user-shield"></em>
                        </div>
                    )}
                    <h1>Verify your Identity</h1>
                </div>

                <div className="dialog-body otp-dialog-body large-dialog-scroll">
                    <p>
                        <strong>
                            Protecting the privacy of your information is our highest priority. To verify your identity, you'll need to request a one-time passcode
                            to your mobile device, via text message or voice call.
                        </strong>
                    </p>

                    <p>
                        <span>Standard text message and voice rates apply.</span>
                    </p>

                    <div className="col-xs-12 otp-button-container">
                        <button type="button" className="btn-default btn-primary otp-button" onClick={(e) => onSubmitTextOption(e)}>
                            <span className="btn-text message">Send Me A Text Message</span>
                        </button>
                        <button type="button" className="btn-default btn-primary otp-button" onClick={(e) => onSubmitCallOption(e)}>
                            <span className="btn-text phone">Call My Phone</span>
                        </button>
                    </div>
                </div>

                <div className="dialog-footer">
                    <div className="row between-xs">
                        <div className="col-xs col-no-gutter flex-grow-null">
                           {!isTUUser &&( <button type="button" className="btn-default btn-link btn-no-padding btn-no-border" onClick={() => onClose()}>
                                <span className="btn-text">{strCloseButtonText}</span>
                            </button>)}
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default L3OTPOptionComponent;
